import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Box, VStack, HStack, Text, Icon, Badge, Spinner } from '@chakra-ui/react';
import { FiActivity } from 'react-icons/fi';
import { motion, useMotionValue, animate } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { authAPI, resolveImageUrl } from '../../services/api';

const MotionBox = motion(Box);
const AUTOPLAY_MS = 5000;
const SWIPE_THRESHOLD = 50;     // px：超過就翻頁
const SWIPE_VELOCITY = 500;     // 快速滑動也翻頁
const SPRING = { type: 'spring', stiffness: 300, damping: 30 };

// 無公告（或載入中/失敗）時維持原本佔位視覺
const Placeholder = () => (
    <Box
        w="100%" h="160px" bgGradient="linear(to-r, blue.400, brand.500)" borderRadius="2xl"
        position="relative" overflow="hidden" boxShadow="md"
        display="flex" alignItems="center" justifyContent="center"
    >
        <VStack spacing={1} color="white" zIndex={1}>
            <Icon as={FiActivity} boxSize={10} opacity={0.9} />
            <Text fontWeight="bold" fontSize="xl">校園公告</Text>
            <Text fontSize="sm" opacity={0.9}>最新活動與重要通知</Text>
        </VStack>
        <Box position="absolute" top="-20px" right="-20px" boxSize="100px" borderRadius="full" bg="whiteAlpha.200" />
        <Box position="absolute" bottom="-30px" left="-10px" boxSize="80px" borderRadius="full" bg="whiteAlpha.200" />
    </Box>
);

// 單張 Banner 內容
const Slide = ({ item }) => {
    const cover = resolveImageUrl(item.cover_image_url);
    return (
        <Box
            flex="0 0 100%"
            minW="100%"
            h="100%"
            position="relative"
            bgGradient={cover ? undefined : 'linear(to-r, blue.400, brand.500)'}
            bgImage={cover ? `url(${cover})` : undefined}
            bgSize="cover"
            bgPosition={item.cover_position || 'center'}
        >
            {/* 圖片上的漸層遮罩，確保文字可讀 */}
            <Box position="absolute" inset={0} bg={cover ? 'blackAlpha.500' : 'transparent'} />
            {item.is_emergency && (
                <Badge position="absolute" top={3} left={3} colorScheme="red" borderRadius="full" px={2} zIndex={2}>
                    緊急
                </Badge>
            )}
            {!item.is_read && (
                <Box position="absolute" top={3} right={3} boxSize="10px" borderRadius="full" bg="red.400" zIndex={2} />
            )}
            <VStack position="absolute" bottom={0} left={0} right={0} align="start" spacing={1} p={4} zIndex={1} color="white">
                <Text fontWeight="bold" fontSize="lg" noOfLines={1}>{item.title}</Text>
                {item.summary && <Text fontSize="sm" opacity={0.9} noOfLines={1}>{item.summary}</Text>}
            </VStack>
        </Box>
    );
};

const AnnouncementBanner = () => {
    const navigate = useNavigate();
    const draggedRef = useRef(false);
    const containerRef = useRef(null);
    const posRef = useRef(1);       // 目前 track slot：1..n 為真實，0=尾克隆、n+1=頭克隆
    const animRef = useRef(null);
    const lockRef = useRef(false);  // 轉場鎖，避免重入造成跳動
    const x = useMotionValue(0);
    const [items, setItems] = useState([]);
    const [realIndex, setRealIndex] = useState(0);
    const [width, setWidth] = useState(0);
    const [ready, setReady] = useState(false);

    const fetchItems = useCallback(async () => {
        try {
            const res = await authAPI.announcements.getActive(5);
            setItems(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setItems([]); // 靜默失敗：維持佔位，不干擾首頁
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => {
        fetchItems();
        const onRefresh = () => fetchItems();
        // App 從背景回前景（例如點推播後）就重抓，確保 Banner 顯示最新公告
        const onVisible = () => { if (document.visibilityState === 'visible') fetchItems(); };
        window.addEventListener('data-refresh', onRefresh);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener('data-refresh', onRefresh);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [fetchItems]);

    const n = items.length;

    // 量測寬度 + 集合變動回到第一則 + 同步定位
    // 用 layout effect 在繪製前定位，避免初次閃到頭/尾克隆張
    useLayoutEffect(() => {
        if (!ready || n === 0) return undefined;
        posRef.current = n > 1 ? 1 : 0; // 集合變動 → 回到第一則（最新）
        setRealIndex(0);
        const measure = () => {
            const w = containerRef.current?.offsetWidth || 0;
            setWidth(w);
            if (w) x.set(-posRef.current * w); // 依目前 slot 同步定位（resize 不重置頁）
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [ready, n, x]);

    // 平滑切到某 slot；抵達頭/尾克隆 → 完成後瞬間瞬移回對應真實格（無縫循環）
    const goTo = useCallback((target) => {
        if (!width || n < 2 || lockRef.current) return;
        lockRef.current = true;
        const real = target === n + 1 ? 1 : (target === 0 ? n : target);
        animRef.current = animate(x, -target * width, {
            ...SPRING,
            onComplete: () => {
                if (real !== target) x.set(-real * width); // 瞬移（同一張畫面，無感）
                posRef.current = real;
                setRealIndex(((real - 1) % n + n) % n);
                lockRef.current = false;
            },
        });
    }, [x, width, n]);

    // 自動輪播（永遠往前，循環無縫）
    useEffect(() => {
        if (n <= 1) return undefined;
        const t = setInterval(() => goTo(posRef.current + 1), AUTOPLAY_MS);
        return () => clearInterval(t);
    }, [n, goTo]);

    if (!ready) {
        return (
            <Box
                w="100%" h="160px" borderRadius="2xl" bg="gray.50" boxShadow="md"
                display="flex" alignItems="center" justifyContent="center"
            >
                <Spinner size="lg" color="blue.400" thickness="3px" />
            </Box>
        );
    }
    if (n === 0) return <Placeholder />;

    // 頭尾克隆：[尾, ...真實, 頭]，達邊界時瞬移回真實格 → 無縫循環
    const loop = n > 1 ? [items[n - 1], ...items, items[0]] : [items[0]];

    return (
        <Box
            ref={containerRef}
            position="relative"
            w="100%" h="160px"
            borderRadius="2xl"
            overflow="hidden"
            boxShadow="md"
            bg="gray.100"
        >
            {/* 整條 track：拖到哪都看得到相鄰訊息，不露白底；頭尾克隆達成無縫循環 */}
            <MotionBox
                display="flex"
                h="100%"
                style={{ x }}
                drag={n > 1 ? 'x' : false}
                dragConstraints={{ left: -(n + 1) * width, right: 0 }}
                dragElastic={0.12}
                onDragStart={() => { draggedRef.current = true; animRef.current?.stop?.(); lockRef.current = false; }}
                onDragEnd={(e, info) => {
                    const off = info.offset.x;
                    const vel = info.velocity.x;
                    if (off < -SWIPE_THRESHOLD || vel < -SWIPE_VELOCITY) {
                        goTo(posRef.current + 1);
                    } else if (off > SWIPE_THRESHOLD || vel > SWIPE_VELOCITY) {
                        goTo(posRef.current - 1);
                    } else {
                        animRef.current = animate(x, -posRef.current * width, SPRING); // 未達門檻 → 彈回
                    }
                    // 延後重置，讓接續的 onTap 讀得到「剛剛在拖曳」而不誤觸導頁
                    setTimeout(() => { draggedRef.current = false; }, 0);
                }}
                onTap={() => {
                    if (!draggedRef.current && items[realIndex]) {
                        navigate(`/parent/announcements/${items[realIndex].id}`);
                    }
                }}
                cursor="pointer"
            >
                {loop.map((item, i) => (
                    <Slide key={`${item.id}-${i}`} item={item} />
                ))}
            </MotionBox>

            {/* 指示點（可點直接跳） */}
            {n > 1 && (
                <HStack position="absolute" bottom={2} left="50%" transform="translateX(-50%)" spacing={1.5} zIndex={3}>
                    {items.map((_, i) => (
                        <Box
                            key={i}
                            boxSize="6px"
                            borderRadius="full"
                            bg={i === realIndex ? 'white' : 'whiteAlpha.600'}
                            cursor="pointer"
                            onClick={() => goTo(i + 1)}
                        />
                    ))}
                </HStack>
            )}
        </Box>
    );
};

// 區域 ErrorBoundary：任何意外（即使 render 出錯）都退回佔位 Banner，
// 確保家長首頁絕不會因為公告 Banner 而壞掉。
class BannerBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error) {
        console.error('AnnouncementBanner error (fallback to placeholder):', error);
    }

    render() {
        return this.state.hasError ? <Placeholder /> : this.props.children;
    }
}

const AnnouncementBannerSafe = () => (
    <BannerBoundary>
        <AnnouncementBanner />
    </BannerBoundary>
);

export default AnnouncementBannerSafe;
